import { useState } from "react";
import { createCheckout, getBillingPlans, getSubscription, verifyPayment } from "./api";
import { useAdminResource } from "./use-admin-resource";
import { useToast } from "../../components/useToast";
import type { BillingCycle, BillingPlanKey, CheckoutSession, PlanInfo } from "./types";

const RAZORPAY_SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";
const SUPPORT_EMAIL = "support@0todeploy.com";

declare global {
  interface Window {
    Razorpay?: new (options: Record<string, unknown>) => { open: () => void };
  }
}

function loadRazorpayScript(): Promise<void> {
  if (window.Razorpay) return Promise.resolve();
  const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT_SRC}"]`);
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Failed to load Razorpay")));
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = RAZORPAY_SCRIPT_SRC;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Razorpay"));
    document.body.appendChild(script);
  });
}

const CYCLE_LABELS: Record<BillingCycle, string> = {
  monthly: "Monthly",
  yearly: "Yearly",
  triennial: "3-Year",
};

function formatRupees(paise: number): string {
  return `₹${(paise / 100).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function formatRupeesPrecise(paise: number): string {
  return `₹${(paise / 100).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

const PLAN_COPY: Record<BillingPlanKey, { label: string; blurb: string }> = {
  basic: { label: "Basic", blurb: "For a single small office." },
  advanced: { label: "Advanced", blurb: "For a busier campus or corporate site." },
};

export function BillingPanel() {
  const {
    data: subscription,
    loading: subLoading,
    error: subError,
    refresh: refreshSubscription,
  } = useAdminResource(() => getSubscription(), []);
  const { data: plans, loading: plansLoading, error: plansError } = useAdminResource(
    () => getBillingPlans(),
    [],
  );
  const { showSuccess, showError } = useToast();

  const [cycle, setCycle] = useState<BillingCycle>("monthly");
  const [subscribingPlan, setSubscribingPlan] = useState<BillingPlanKey | null>(null);
  const [pendingCheckout, setPendingCheckout] = useState<CheckoutSession | null>(null);
  const [payingNow, setPayingNow] = useState(false);

  const plansByKey = new Map<string, PlanInfo>(
    (plans ?? []).map((entry) => [`${entry.plan}-${entry.billing_cycle}`, entry]),
  );

  async function subscribe(plan: BillingPlanKey) {
    setSubscribingPlan(plan);
    try {
      const order = await createCheckout(plan, cycle);
      setPendingCheckout(order);
    } catch (requestError) {
      showError(requestError instanceof Error ? requestError.message : "Failed to start checkout");
    } finally {
      setSubscribingPlan(null);
    }
  }

  async function payNow() {
    if (!pendingCheckout) return;
    const order = pendingCheckout;
    setPayingNow(true);
    try {
      await loadRazorpayScript();

      if (!window.Razorpay) {
        throw new Error("Razorpay checkout could not be loaded");
      }

      const checkout = new window.Razorpay({
        key: order.razorpay_key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.razorpay_order_id,
        name: "0todeploy VMS",
        description: `${PLAN_COPY[order.plan as BillingPlanKey].label} plan — ${CYCLE_LABELS[order.billing_cycle as BillingCycle]}`,
        theme: { color: "#ea580c" },
        handler: (response: {
          razorpay_order_id: string;
          razorpay_payment_id: string;
          razorpay_signature: string;
        }) => {
          void (async () => {
            try {
              await verifyPayment(
                response.razorpay_order_id,
                response.razorpay_payment_id,
                response.razorpay_signature,
              );
              showSuccess(`Subscribed to ${PLAN_COPY[order.plan as BillingPlanKey].label}`);
              setPendingCheckout(null);
              await refreshSubscription();
            } catch (verifyError) {
              showError(
                verifyError instanceof Error
                  ? verifyError.message
                  : "Payment succeeded but activation failed — contact support",
              );
            }
          })();
        },
        modal: {
          ondismiss: () => setPayingNow(false),
        },
      });
      checkout.open();
    } catch (requestError) {
      showError(requestError instanceof Error ? requestError.message : "Failed to open checkout");
      setPayingNow(false);
    }
  }

  const loading = subLoading || plansLoading;

  return (
    <article className="panel">
      <header className="panel-header">
        <h2>Billing</h2>
        <span>{subscription?.plan ? `${subscription.plan} plan` : "No plan"}</span>
      </header>

      {subError ? <p className="error-banner">{subError}</p> : null}
      {plansError ? <p className="error-banner">{plansError}</p> : null}

      {!loading && subscription ? (
        <div className="aging-grid">
          <div className="aging-card">
            <p className="stat-label">Current Plan</p>
            <p className="stat-value" style={{ fontSize: "1.4rem" }}>
              {subscription.plan ? PLAN_COPY[subscription.plan as BillingPlanKey]?.label ?? subscription.plan : "None"}
            </p>
          </div>
          <div className="aging-card">
            <p className="stat-label">Visits This Month</p>
            <p className="stat-value" style={{ fontSize: "1.4rem" }}>
              {subscription.visit_count_this_month}
              {subscription.visitor_cap ? ` / ${subscription.visitor_cap}` : ""}
            </p>
            <p className="field-hint">{subscription.visitor_count_this_month} new visitors</p>
          </div>
          <div className="aging-card">
            <p className="stat-label">{subscription.is_active ? "Renews" : "Expired"}</p>
            <p className="stat-value" style={{ fontSize: "1.4rem" }}>
              {formatDate(subscription.current_period_end)}
            </p>
          </div>
        </div>
      ) : null}

      <div className="screen-switch">
        {(Object.keys(CYCLE_LABELS) as BillingCycle[]).map((option) => (
          <button
            key={option}
            type="button"
            className={option === cycle ? "screen-btn active" : "screen-btn"}
            onClick={() => setCycle(option)}
          >
            {CYCLE_LABELS[option]}
          </button>
        ))}
      </div>

      <div className="gate-pass-template-grid">
        {(["basic", "advanced"] as BillingPlanKey[]).map((plan) => {
          const info = plansByKey.get(`${plan}-${cycle}`);
          const isCurrent = subscription?.plan === plan && subscription?.is_active;
          return (
            <article key={plan} className="gate-pass-template-card">
              <div className="gate-pass-preview" style={{ borderColor: "#ea580c" }}>
                <p className="gate-pass-preview-brand" style={{ color: "#ea580c" }}>
                  {PLAN_COPY[plan].label}
                </p>
                <p className="gate-pass-preview-name">{info ? formatRupees(info.amount) : "—"}</p>
                <p className="gate-pass-preview-meta">
                  {CYCLE_LABELS[cycle]}
                  {info?.visitor_cap ? ` · up to ${info.visitor_cap} visitors/mo` : ""}
                </p>
                <p className="gate-pass-preview-footer">{PLAN_COPY[plan].blurb}</p>
              </div>
              <button
                className="refresh-btn action-submit"
                type="button"
                disabled={subscribingPlan === plan || isCurrent}
                onClick={() => void subscribe(plan)}
                style={{ width: "100%" }}
              >
                {isCurrent
                  ? "Current Plan"
                  : subscribingPlan === plan
                    ? "Opening checkout..."
                    : `Subscribe to ${PLAN_COPY[plan].label}`}
              </button>
            </article>
          );
        })}

        <article className="gate-pass-template-card">
          <div className="gate-pass-preview" style={{ borderColor: "#0a0a0a" }}>
            <p className="gate-pass-preview-brand">Custom</p>
            <p className="gate-pass-preview-name">Contact us</p>
            <p className="gate-pass-preview-meta">Volume, multi-location, or custom terms</p>
            <p className="gate-pass-preview-footer">Negotiated pricing set up by 0todeploy</p>
          </div>
          <a
            className="refresh-btn action-submit"
            href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("Custom VMS plan enquiry")}`}
            style={{ width: "100%", textAlign: "center", textDecoration: "none", boxSizing: "border-box" }}
          >
            Contact Sales
          </a>
        </article>
      </div>
      <p className="field-hint">Prices shown are exclusive of GST and payment charges — see the full breakdown before you pay.</p>

      {pendingCheckout ? (
        <div className="action-form action-card">
          <label className="toolbar-label">
            Confirm payment — {PLAN_COPY[pendingCheckout.plan as BillingPlanKey].label} plan,{" "}
            {CYCLE_LABELS[pendingCheckout.billing_cycle as BillingCycle]}
          </label>
          <div className="checkin-fields" style={{ flexDirection: "column", alignItems: "stretch", gap: "0.4rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Plan price</span>
              <span>{formatRupeesPrecise(pendingCheckout.base_amount)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>GST (18%)</span>
              <span>{formatRupeesPrecise(pendingCheckout.gst_amount)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Payment charges</span>
              <span>{formatRupeesPrecise(pendingCheckout.payment_charge_amount)}</span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                borderTop: "1px solid var(--border)",
                paddingTop: "0.4rem",
                fontWeight: 700,
              }}
            >
              <span>Total</span>
              <span>{formatRupeesPrecise(pendingCheckout.amount)}</span>
            </div>
          </div>
          <div className="action-row" style={{ marginTop: "0.75rem" }}>
            <button
              className="refresh-btn action-submit"
              type="button"
              disabled={payingNow}
              onClick={() => void payNow()}
            >
              {payingNow ? "Opening checkout..." : `Proceed to Pay ${formatRupeesPrecise(pendingCheckout.amount)}`}
            </button>
            <button
              className="refresh-btn danger-action-btn"
              type="button"
              disabled={payingNow}
              onClick={() => setPendingCheckout(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </article>
  );
}
